import { useEffect, useState } from "react";
import { Download, ImageIcon, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { debateCard } from "../lib/api";

const FORMATS = [
  ["post", "Instagram post", "1080×1350"],
  ["story", "Instagram story", "1080×1920"],
  ["youtube", "YouTube", "1280×720"],
];

const CardDialog = ({ open, onOpenChange, chatId, index }) => {
  const [fmt, setFmt] = useState("post");
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setUrl(null);
    debateCard(chatId, index, fmt).then((b) => { if (live) { setBlob(b); setUrl(URL.createObjectURL(b)); } })
      .catch(() => toast.error("Couldn't make the card. Try again."));
    return () => { live = false; };
  }, [open, fmt, chatId, index]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  const file = () => new File([blob], `what-debate-${fmt}.png`, { type: "image/png" });
  const download = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = `what-debate-${fmt}.png`;
    a.click();
  };
  const share = async () => {
    try {
      await navigator.share({ files: [file()], title: "My WHAT? debate", text: "I debated the AIs on askwhat.co.za" });
    } catch (e) {
      if (e?.name !== "AbortError") download();
    }
  };
  const canShare = typeof navigator !== "undefined" && navigator.canShare && blob && navigator.canShare({ files: [file()] });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-card-dialog" className="max-w-xl max-h-[92vh] overflow-y-auto bg-[#0E131D] border-white/[0.08] text-slate-200 p-6 sm:p-8 font-['Plus_Jakarta_Sans',sans-serif]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-white">Share this debate</DialogTitle>
          <DialogDescription className="text-slate-400">Pick a size, then download or share the image.</DialogDescription>
        </DialogHeader>
        <div role="radiogroup" aria-label="Card size" className="flex flex-wrap gap-2">
          {FORMATS.map(([k, label, size]) => (
            <button key={k} type="button" role="radio" aria-checked={fmt === k} data-testid={`card-format-${k}`} onClick={() => setFmt(k)}
              className={`px-3 py-2 rounded-lg text-xs border transition-colors ${fmt === k ? "border-[#D4FF3A]/60 bg-[#D4FF3A]/10 text-[#D4FF3A]" : "border-white/[0.08] text-slate-400 hover:text-white"}`}>
              {label} <span className="font-mono opacity-60">{size}</span>
            </button>
          ))}
        </div>
        <div className="mt-4 rounded-xl bg-black/30 border border-white/[0.06] grid place-items-center min-h-[260px] p-3">
          {url ? <img data-testid="share-card-preview" src={url} alt="Debate share card preview" className="max-h-[52vh] w-auto rounded-lg" />
            : <Loader2 className="animate-spin text-slate-500" />}
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <button data-testid="share-card-download-btn" onClick={download} disabled={!url} className="what-btn-primary flex items-center gap-2"><Download size={15} /> Download image</button>
          {canShare && <button data-testid="share-card-share-btn" onClick={share} className="what-btn-ghost flex items-center gap-2"><Share2 size={15} /> Share…</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ShareCardButton = ({ chatId, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={`share-card-btn-${index}`} onClick={() => setOpen(true)} className="what-surprise mt-4">
        <ImageIcon size={15} /> Share as image
      </button>
      {open && <CardDialog open={open} onOpenChange={setOpen} chatId={chatId} index={index} />}
    </>
  );
};
